import { useQuery, useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

import { setPartQueryData } from "#modules/parts/infra/part.query";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import {
  getQuoteRequestLineQueryData,
  setQuoteRequestLineQueryData,
} from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { useBulkQueryCache } from "#shared/hooks/useBulkQueryCache";

import { quoteRequestLineQuery } from "../infra/quoteRequestLine.query";
import { quoteRequestLinesBulkQuery } from "../infra/quoteRequestLinesBulk.query";

/**
 * Optimized hook to fetch multiple quote request lines in a single API call
 * instead of making N individual requests. This significantly reduces
 * network overhead and improves page load times.
 *
 * The hook is reactive to individual query updates. When a single quote request
 * line query is invalidated and refetched, the returned data automatically updates.
 */
export const useAllQuoteRequestLinesBulk = (
  initialQuoteRequestLineIds: string[],
  quoteRequestLineIds: string[],
) => {
  const {
    data,
    isInitialLoading: isBulkQueryInitialLoading,
    error,
  } = useQuery({
    ...quoteRequestLinesBulkQuery({ ids: initialQuoteRequestLineIds }),
  });

  const { isInitialLoading } = useBulkQueryCache({
    isBulkLoading: isBulkQueryInitialLoading,
    bulkData: data,
    isBulkEnabled: initialQuoteRequestLineIds.length > 0,
    initialIds: initialQuoteRequestLineIds,
    currentIds: quoteRequestLineIds,
    onPopulateCache: (bulkData, { queryClient, populatedKeys }) => {
      bulkData.forEach((quoteRequestLine) => {
        if (!populatedKeys.has(quoteRequestLine.id)) {
          const existingData = getQuoteRequestLineQueryData(queryClient, quoteRequestLine.id);

          if (!existingData) {
            setQuoteRequestLineQueryData(queryClient, quoteRequestLine.id, quoteRequestLine);
            setPartQueryData(queryClient, quoteRequestLine.partId, quoteRequestLine.part);
          }

          populatedKeys.add(quoteRequestLine.id);
        }
      });
    },
  });

  const quoteRequestLineQueries = useQueries({
    queries: quoteRequestLineIds.map((id) => ({
      ...quoteRequestLineQuery({ id }),
      enabled: !isInitialLoading,
    })),
  });

  const quoteRequestLines = useMemo(() => {
    return quoteRequestLineQueries
      .map((quoteRequestLineQueryResult) => quoteRequestLineQueryResult.data)
      .filter(
        (quoteRequestLine): quoteRequestLine is QuoteRequestLineDto =>
          quoteRequestLine !== null && quoteRequestLine !== undefined,
      );
  }, [quoteRequestLineQueries]);

  return {
    quoteRequestLines,
    isInitialLoading,
    error,
  };
};
